/** Add an angle / Find seeds for an angle — a non-modal side sheet beside Curate (facets 1c-3;
 *  setup board ⑤, spec 2026-10-29 §1–§2). Curate stays visible and usable behind it; outside
 *  clicks never close it (✕ / Escape do, except while Start's lock holds).
 *  ① Describe: draft (describe / own words / from a paper / Coverage Audit gap) + the scope line
 *  (inherited, or "Change" → years · recency · field overrides inline) → ② Seeds (discovered from
 *  the angle's own text, anchor pinned, search fallback; seed-status warnings from useSeedPreview)
 *  → ③ Check & add (sample grade: overall + this angle). "What it adds" (new-paper count) follows
 *  the steps; its line 1 is the fit estimate (new mode: how much of the library already fits the
 *  draft, sampled papers that fit tagged in Curate when `tagList`). Seeds mode is ② only.
 *  While Start's lock holds, route navigation is held too (useBlocker; "Stay / Leave anyway").
 *  Start (`new`): project-question review over [...active, draft] → useAddAngle (resumable). */
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Dialog as DialogPrimitive } from "radix-ui";
import { useBlocker } from "@tanstack/react-router";
import { Check, Loader2, X } from "lucide-react";
import { ProjectQuestionReview } from "@/components/angles/ProjectQuestionReview";
import { type AddAngleState, RUNNING_STATES, useAddAngle } from "@/components/angles/useAddAngle";
import { newPaperCount, useSeedPreview } from "@/components/angles/useSeedPreview";
import { WhatItAdds } from "@/components/angles/WhatItAdds";
import { toAnchorCandidate } from "@/components/setup/AngleStep";
import { httpError } from "@/components/setup/http";
import { type PickedPaper, PaperSearchBox } from "@/components/setup/PaperSearchBox";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { usePaperKeys } from "@/hooks/usePaperKeys";
import { discoveryTargetKey, useSeedDiscovery } from "@/hooks/useSeedDiscovery";
import { useDraftAngle, useEstimateFit, useSampleGradeDraft } from "@/hooks/useSetup";
import type { FacetCreateVars } from "@/hooks/useFacets";
import { effectiveScope } from "@/lib/angles";
import { trackEvent } from "@/lib/engagement";
import { FIELD_GROUPS, FIELD_KEYS } from "@/lib/setup/fields";
import { mergeNetworks, paperKey, pickSample, type PreviewPaper, quotaNote } from "@/lib/setup/preview";
import { useAngleFitPreviewStore } from "@/stores/angle-fit-preview-store";
import { type SeedCandidate, useSeedDiscoveryStore } from "@/stores/seed-discovery-store";
import { useSideSheetStore } from "@/stores/side-sheet-store";
import { isSettled, netKey, networksFor, type SeedRef, useSetupStore } from "@/stores/setup-store";
import type {
  Concept, ConceptScope, DiscoveryTarget, DraftGrade, EstimateFit, FacetSummary, FieldKey, GapDraftInput, ProjectQuestion, Recency,
} from "@/types/concept";

export type AddAngleMode =
  | { kind: "new"; prefill?: { gap: GapDraftInput } }
  | { kind: "seeds"; facet: FacetSummary };

export interface AddAngleSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  concept: Concept;
  mode: AddAngleMode;
  onDone?: (facetId: string) => void;
  /** Tag the sampled fits in the Curate list (Curate's AngleManager only). */
  tagList?: boolean;
}

type Step = 1 | 2 | 3;
type DraftSource = FacetCreateVars["source"];
interface DraftState {
  label: string;
  question: string;
  search_terms: string;
  auto_drafted: boolean;
  source: DraftSource;
}

const LABEL_MAX = 80;
const QUESTION_MAX = 2000;
const TERMS_MAX = 1000;
const STEP_NAMES: Record<Step, string> = { 1: "Describe", 2: "Seeds", 3: "Check & add" };
const STEPS: Step[] = [1, 2, 3];
/** Discovery candidates pre-checked at ② (by overlap_score). */
const PRECHECK = 5;
const SAMPLE_SIZE = 8;

const RECENCY: Recency[] = ["classic", "balanced", "recent"];
const RECENCY_LABEL: Record<Recency, string> = { classic: "Classic", balanced: "Balanced", recent: "Recent" };
// Mirrors backend core/scope.py ConceptScope bounds (1800 … next year).
const YEAR_MIN = 1800;
const YEAR_MAX = new Date().getFullYear() + 1;
const yearOk = (y: number | undefined) => y == null || (Number.isInteger(y) && y >= YEAR_MIN && y <= YEAR_MAX);
const toYear = (v: string) => (v.trim() === "" ? undefined : Number(v));

function yearsText(s: ConceptScope | null | undefined): string {
  const lo = s?.year_min;
  const hi = s?.year_max;
  if (lo != null && hi != null) return `${lo}–${hi}`;
  if (lo != null) return `${lo} onward`;
  if (hi != null) return `Up to ${hi}`;
  return "Any year";
}
const recencyText = (s: ConceptScope | null | undefined) => RECENCY_LABEL[s?.recency ?? "balanced"];
const fieldText = (s: ConceptScope | null | undefined) => (s?.field ? FIELD_GROUPS[s.field].label : "Any field");

const scopeText = (s: ConceptScope | null | undefined) => `${yearsText(s)} · ${recencyText(s)} · ${fieldText(s)}`;
/** A sampled paper "fits" the draft at this angle score (as Curate's angle bands). */
const FIT_MIN = 50;
/** What the fit estimate is keyed on: scope doesn't change fit, search terms don't either. */
const fitKeyOf = (d: { label: string; question: string }) => JSON.stringify([d.label.trim(), d.question.trim()]);
interface FitState {
  key: string;
  label: string;
  status: "running" | "done" | "error";
  result?: EstimateFit;
  /** A 429's server detail (other errors offer Try again). */
  detail?: string;
}
const NO_SEEDS: SeedRef[] = [];

/** Drop undefined keys so "inherit" never travels as an explicit key. */
function overridesOnly(scope: ConceptScope): ConceptScope {
  return Object.fromEntries(Object.entries(scope).filter(([, v]) => v !== undefined)) as ConceptScope;
}

function draftFromFacet(f: FacetSummary): DraftState {
  return { label: f.label, question: f.question, search_terms: f.search_terms, auto_drafted: false, source: "add_angle" };
}

const RUNNING_COPY: Partial<Record<AddAngleState, string>> = {
  creating: "Adding the angle…",
  seeding: "Adding the seeds…",
  trawling: "Following the seeds' citations…",
  scoring: "Starting scoring…",
};

function fitLineText(r: EstimateFit): string {
  const graded = r.sampled.filter((x) => x.angle_score != null).length;
  // "~0 of your 334" reads as a broken number; say what was actually seen.
  if (r.fit_count === 0) return r.exact ? `None of your ${r.eligible} papers fit` : `None of the ${graded} sampled paper${graded === 1 ? "" : "s"} fit`;
  return r.exact
    ? `${r.estimate} of your ${r.eligible} papers already fit`
    : `~${r.estimate} of your ${r.eligible} papers already fit · estimated from ${graded}`;
}

export function AddAngleSheet({ open, onOpenChange, concept, mode, onDone, tagList = false }: AddAngleSheetProps) {
  // The root layout reserves the sheet's width while it's open (list + paper detail stay visible).
  useEffect(() => {
    if (!open) return;
    useSideSheetStore.getState().setOpen(true);
    return () => useSideSheetStore.getState().setOpen(false);
  }, [open]);
  const title = mode.kind === "seeds" ? `Find seeds for ${mode.facet.label}` : "Add an angle";
  // Start can't be abandoned half-way (Decision 6): close, overlay and Escape are ignored while it
  // runs — and after a failure that left a saved angle behind (reopening would make a second one;
  // the body offers an explicit "Leave it" instead).
  // Why Close is disabled (null = it isn't); shown under the header, since a disabled ✕ can't explain itself.
  const [lockReason, setLockReason] = useState<string | null>(null);
  const locked = lockReason !== null;
  const lockedRef = useRef(false);
  const onLocked = useCallback((reason: string | null) => { lockedRef.current = reason !== null; setLockReason(reason); }, []);
  // Non-modal: the app's links stay clickable. While the lock holds, a route change waits for an
  // inline "Stay / Leave anyway" (spec §8.6) — never a browser confirm().
  const blocker = useBlocker({
    shouldBlockFn: () => lockedRef.current,
    enableBeforeUnload: () => lockedRef.current,
    withResolver: true,
  });
  const blockerRef = useRef(blocker);
  useEffect(() => { blockerRef.current = blocker; });
  const close = useCallback(() => {
    lockedRef.current = false;
    setLockReason(null);
    // A navigation held while Start ran goes ahead once the sheet closes (the parent may unmount it).
    if (blockerRef.current.status === "blocked") blockerRef.current.proceed();
    onOpenChange(false);
  }, [onOpenChange]);
  const contentRef = useRef<HTMLDivElement>(null);
  return (
    // Non-modal (board ⑤ §1): no overlay, no focus trap, the list behind stays scrollable and
    // clickable. Outside interactions never close it — the user is meant to look at the list.
    // z-[61]: above the fixed OperationBar (z-[60]), which otherwise covers the sheet's footer.
    <DialogPrimitive.Root modal={false} open={open} onOpenChange={(o) => { if (!o && lockedRef.current) return; onOpenChange(o); }}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Content
          ref={contentRef}
          onInteractOutside={(e) => e.preventDefault()}
          onOpenAutoFocus={(e) => {
            // First field, not the ✕ (the header's first focusable).
            const field = contentRef.current?.querySelector<HTMLElement>("[data-testid=add-angle-sheet] :is(textarea, input, button[role=checkbox])");
            if (field) { e.preventDefault(); field.focus(); }
          }}
          className="fixed inset-y-0 right-0 z-[61] flex w-full flex-col border-l bg-background text-foreground shadow-xl focus:outline-none sm:w-[480px] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right"
        >
          <div className="flex items-start justify-between border-b px-5 py-4">
            <div>
              <DialogPrimitive.Title className="text-base font-semibold">{title}</DialogPrimitive.Title>
              <DialogPrimitive.Description className="mt-0.5 text-xs text-muted-foreground">
                {mode.kind === "seeds"
                  ? "Seed papers lead this angle's search."
                  : "A new angle is graded alongside your others."}
              </DialogPrimitive.Description>
            </div>
            <DialogPrimitive.Close asChild>
              <button aria-label="Close" disabled={locked} className="text-muted-foreground hover:text-foreground disabled:opacity-50">
                <X className="size-5" />
              </button>
            </DialogPrimitive.Close>
          </div>
          {(lockReason || blocker.status === "blocked") && (
            <div className="flex flex-col gap-2 border-b px-5 py-2 text-xs text-muted-foreground">
              {lockReason && <p>{lockReason}</p>}
              {blocker.status === "blocked" && (
                <div aria-live="assertive" className="flex flex-col gap-2 text-sm text-foreground">
                  <p>Leaving now would stop adding this angle.</p>
                  <div className="flex justify-end gap-2">
                    <Button size="sm" variant="outline" onClick={() => blocker.proceed()}>Leave anyway</Button>
                    <Button size="sm" onClick={() => blocker.reset()}>Stay</Button>
                  </div>
                </div>
              )}
            </div>
          )}
          {/* Content unmounts on close, so the body's state resets on every open. */}
          <SheetBody concept={concept} mode={mode} onDone={onDone} onLocked={onLocked} onClose={close} tagList={tagList} />
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

function SheetBody({ concept, mode, onDone, onLocked, onClose, tagList }: Pick<AddAngleSheetProps, "concept" | "mode" | "onDone"> & {
  onLocked: (reason: string | null) => void;
  onClose: () => void;
  tagList: boolean;
}) {
  const seedsOnly = mode.kind === "seeds";
  const gap = mode.kind === "new" ? mode.prefill?.gap : undefined;
  const baseSource: DraftSource = gap ? "coverage_audit" : "add_angle";
  const [step, setStep] = useState<Step>(mode.kind === "seeds" ? 2 : 1);
  const [draft, setDraft] = useState<DraftState | null>(mode.kind === "seeds" ? draftFromFacet(mode.facet) : null);
  const [anchor, setAnchor] = useState<SeedCandidate | null>(null);
  const [scope, setScope] = useState<ConceptScope>(mode.kind === "seeds" ? (mode.facet.scope ?? {}) : {});
  /** ① scope controls expanded ("Change"). */
  const [scopeOpen, setScopeOpen] = useState(false);
  const [text, setText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const draftAngle = useDraftAngle();
  // Drafting renders from local state fed by the request's promise, not draftAngle.isPending:
  // under StrictMode (mount→cleanup→mount) a mutation fired at mount loses its observer, and
  // isPending would stick (spinner forever, "Draft it" disabled). Same pattern as ProjectQuestionReview.
  const draftReq = useRef<{ key: string; promise: ReturnType<typeof draftAngle.mutateAsync> } | null>(null);
  const [drafting, setDrafting] = useState(false);
  // ② — seeds picked by hand (search fallback) and the checked set (keys = paper_id).
  const [extra, setExtra] = useState<SeedCandidate[]>([]);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [sampleReady, setSampleReady] = useState(false);
  const { discover } = useSeedDiscovery(concept.id);
  const { data: me } = useCurrentUser();
  const storeKey = useSeedDiscoveryStore((s) => s.targetKey);
  const storeCandidates = useSeedDiscoveryStore((s) => s.candidates);
  const storeRunning = useSeedDiscoveryStore((s) => s.isRunning);
  const storePhase = useSeedDiscoveryStore((s) => s.phase);
  const storeError = useSeedDiscoveryStore((s) => s.error);
  const setupStatus = useSetupStore((s) => s.status);
  /** Key of the last run this sheet started; close resets the store only if it still holds it. */
  const ranKey = useRef<string | null>(null);
  /** The release cleanup's deferred resetIfOwned (StrictMode-safe; see the release effect). */
  const pendingReset = useRef<{ key: string; timer: ReturnType<typeof setTimeout> } | null>(null);
  const precheckedFor = useRef<string | null>(null);
  const addAngle = useAddAngle(concept, mode);
  const running = RUNNING_STATES.includes(addAngle.state);
  /** The angle this sheet created (new mode). Once set, Start only resumes: ① edits can't apply. */
  const createdId = mode.kind === "new" ? addAngle.facetId : null;
  /** A failure after the angle was saved: closing (then reopening) would make a second angle. */
  const strandedId = createdId && addAngle.state === "error" ? createdId : null;
  const [reviewing, setReviewing] = useState(false);
  /** The project question of the last Start, so Retry resumes with the same choices. */
  const lastPq = useRef<ProjectQuestion | undefined>(undefined);
  /** Guards a double-click on the review's submit (the second must not touch `reviewing`). */
  const runBusy = useRef(false);
  /** onClose/onDone only while mounted (a parent may unmount the sheet mid-Start). */
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  /** A concept-wide discovery owned the store: starting ours would kill it (newRun). */
  const [blocked, setBlocked] = useState(false);

  useEffect(() => {
    onLocked(running ? "Starting can't be stopped half-way. This closes when it's done."
      : strandedId ? "This angle is already saved. Retry, or choose “Leave it” below." : null);
  }, [running, strandedId, onLocked]);

  type DraftBody = Parameters<typeof draftAngle.mutateAsync>[0];

  /** Render one draft request's outcome — only while it is still the latest (`live`). */
  async function settleDraft(promise: ReturnType<typeof draftAngle.mutateAsync>, body: DraftBody, source: DraftSource, live: () => boolean) {
    setError(null);
    setDrafting(true);
    try {
      const d = await promise;
      if (!live()) return;
      setDraft({
        label: d.label.slice(0, LABEL_MAX), question: d.question.slice(0, QUESTION_MAX),
        search_terms: d.search_terms.slice(0, TERMS_MAX), auto_drafted: true, source,
      });
    } catch (err) {
      const { status, detail } = await httpError(err);
      if (!live()) return;
      if (status === 422 && detail === "no_abstract") {
        setError("We couldn't find an abstract for that paper. Describe the angle in a sentence or two instead; we'll still use the paper as a seed.");
      } else if (body.gap) {
        // Fall back to the gap's own words so the user can still edit and continue.
        setDraft({
          label: body.gap.tag.slice(0, LABEL_MAX), question: body.gap.description.slice(0, QUESTION_MAX),
          search_terms: body.gap.suggested_queries.join(", ").slice(0, TERMS_MAX), auto_drafted: false, source,
        });
        setError("Couldn't draft that. We filled in the gap's own words; edit anything that's off.");
      } else {
        setError("Couldn't draft that. Write it in your own words, or try again.");
      }
    } finally {
      if (live()) setDrafting(false);
    }
  }

  function runDraft(body: DraftBody, source: DraftSource) {
    const req = { key: JSON.stringify(body), promise: draftAngle.mutateAsync(body) };
    draftReq.current = req; // a newer draft supersedes an older one still in flight
    void settleDraft(req.promise, body, source, () => draftReq.current === req);
  }

  // Gap prefill: draft once per open. The in-flight ref keeps StrictMode's second mount on the
  // first request (no second LLM call); `live` lets only the current mount render it.
  useEffect(() => {
    if (!gap) return;
    const body: DraftBody = { name: concept.name, gap };
    const key = JSON.stringify(body);
    if (draftReq.current?.key !== key) draftReq.current = { key, promise: draftAngle.mutateAsync(body) };
    const req = draftReq.current;
    let live = true;
    void settleDraft(req.promise, body, "coverage_audit", () => live && draftReq.current === req);
    return () => { live = false; };
    // Once per open (the body remounts on each open).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function useMyWords() {
    const t = text.trim();
    setError(null);
    setDraft({
      label: t.split(/\s+/).slice(0, 6).join(" ").slice(0, LABEL_MAX), question: t.slice(0, QUESTION_MAX),
      search_terms: "", auto_drafted: false, source: anchor ? "from_paper" : baseSource,
    });
  }

  function pickPaper(p: PickedPaper) {
    const a = toAnchorCandidate(p);
    setAnchor(a);
    // The anchor is a seed in its own right: checked whatever discovery finds.
    setChecked((cur) => new Set(cur).add(a.paper_id));
    void runDraft({
      name: concept.name,
      paper: { title: p.title, year: p.year, abstract: p.abstract, tldr: p.tldr?.text ?? null, doi: p.externalIds?.DOI ?? null },
    }, "from_paper");
  }

  const draftValid = !!draft?.label.trim() && !!draft?.question.trim();

  // ── Fit estimate (line 1 of What it adds; spec §3 "When it runs", §8.9, §8.10) ──
  const estimateFit = useEstimateFit(concept.id);
  // Rendered from local state fed by the request's promise, never estimateFit.isPending (StrictMode).
  const [fit, setFit] = useState<FitState | null>(null);
  /** The current request (body key + token). Set before the first await, so StrictMode's second
   *  mount — and every later render — sees a request exists and never auto-fires another. */
  const fitReq = useRef<{ key: string; token: number } | null>(null);
  const fitToken = useRef(0);
  const conceptIdRef = useRef(concept.id);
  useEffect(() => { conceptIdRef.current = concept.id; }, [concept.id]);
  const fitKey = !seedsOnly && draft && draftValid ? fitKeyOf(draft) : null;
  /** The draft changed since the estimate (or its request) was made: dim it, offer Re-estimate. */
  const fitEdited = !!fit && (!draft || fitKeyOf(draft) !== fit.key);

  function runEstimate(d: { label: string; question: string }) {
    const body = { label: d.label.trim(), question: d.question.trim() };
    const key = fitKeyOf(body);
    const token = ++fitToken.current;
    fitReq.current = { key, token };
    const conceptId = concept.id;
    // Applies only while mounted, still the latest request, and for the same concept (§8.9).
    const own = () => mounted.current && fitReq.current?.token === token && conceptIdRef.current === conceptId;
    setFit({ key, label: body.label, status: "running" });
    estimateFit.mutateAsync(body).then(
      (result) => {
        if (!own()) return;
        setFit({ key, label: body.label, status: "done", result });
        trackEvent("angle_fit_estimated", {
          concept_id: conceptId, eligible: result.eligible,
          graded: result.sampled.filter((x) => x.angle_score != null).length,
          fit_count: result.fit_count, estimate: result.estimate, exact: result.exact,
        });
      },
      async (err) => {
        const { status, detail } = await httpError(err);
        if (!own()) return;
        setFit({
          key, label: body.label, status: "error",
          detail: status === 429 ? (detail ?? "Estimate limit reached for today.") : undefined,
        });
      },
    );
  }

  // Automatically once, when the first (valid) draft appears. Edits never re-run it (Re-estimate does).
  useEffect(() => {
    if (!fitKey || !draft || fitReq.current) return;
    runEstimate(draft);
    // Keyed on the draft body; `draft` is read for that body.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitKey]);

  // Row tags (Curate only): the sampled papers that fit, while the estimate describes the current
  // draft. An edit, a new request, close/unmount and a concept change all clear them (§4, §8.10).
  const fitIds = tagList && fit?.status === "done" && fit.result && !fitEdited
    ? fit.result.sampled.filter((x) => x.angle_score != null && x.angle_score >= FIT_MIN).map((x) => x.paper_id)
    : [];
  const tagsKey = fitIds.length && fit ? JSON.stringify([concept.id, fit.label, fitIds]) : null;
  /** This sheet wrote the store (so clearing can't wipe another sheet's or concept's tags). */
  const ownTags = useRef(false);
  const clearTags = useCallback((conceptId: string) => {
    if (!ownTags.current) return;
    ownTags.current = false;
    const s = useAngleFitPreviewStore.getState();
    if (s.conceptId === conceptId) s.clear();
  }, []);
  useEffect(() => {
    if (!tagsKey || !fit) return;
    const conceptId = concept.id;
    useAngleFitPreviewStore.getState().set(conceptId, fitIds, fit.label);
    ownTags.current = true;
    return () => clearTags(conceptId);
    // tagsKey covers concept id, label and ids.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tagsKey]);
  const outOfBounds = !yearOk(scope.year_min) || !yearOk(scope.year_max);
  const effMin = scope.year_min ?? concept.scope?.year_min;
  const effMax = scope.year_max ?? concept.scope?.year_max;
  const inverted = effMin != null && effMax != null && effMin > effMax;
  const scopeValid = !outOfBounds && !inverted;
  const firstStep: Step = mode.kind === "seeds" ? 2 : 1;
  const overridden = Object.keys(overridesOnly(scope)).length > 0;
  /** Invalid years keep the controls open, so the error stays in view. */
  const scopeShown = scopeOpen || !scopeValid;

  // ② discovery target: the sheet's own angle (draft + scope overrides) or the saved facet.
  const target: DiscoveryTarget | null = useMemo(() => {
    if (mode.kind === "seeds") return { facet_id: mode.facet.id };
    if (!draft) return null;
    return { angle: { label: draft.label, question: draft.question, search_terms: draft.search_terms, scope: overridesOnly(scope) } };
  }, [mode, draft, scope]);
  const targetKey = target ? discoveryTargetKey(target) : null;
  // The store is global: only a run for this exact target is ours to show.
  const own = targetKey !== null && storeKey === targetKey;
  const candidates = own ? storeCandidates : null;

  /** Claim the store for this sheet's target — unless a concept-wide discovery is running there
   *  (newRun would supersede it, and Curate would lose its run). Rediscover also re-arms the precheck. */
  function claimDiscovery(force: boolean) {
    if (!target || !targetKey) return;
    const s = useSeedDiscoveryStore.getState();
    if (s.owner === "concept" && s.isRunning) { setBlocked(true); return; }
    setBlocked(false);
    ranKey.current = targetKey;
    if (force) precheckedFor.current = null;
    void discover(force, target);
  }

  useEffect(() => {
    if (step !== 2 || !target || !targetKey || ranKey.current === targetKey) return;
    claimDiscovery(false);
    // Once per target per open; `target` is derived from targetKey's inputs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, targetKey, discover]);

  // Release our run when the target changes or the body unmounts — deferred a tick, so StrictMode's
  // simulated unmount→remount keeps the run: the remount cancels the pending reset for the same key
  // and the effect above skips (ranKey still set) instead of POSTing a second discovery for the same
  // target (the backend 409s it). A real close/unmount lets the timer fire. Not keyed on `step`, so
  // Seeds → Check & add keeps the candidates (Check & add starts with them).
  useEffect(() => {
    if (!targetKey) return;
    const pending = pendingReset.current;
    if (pending?.key === targetKey) {
      clearTimeout(pending.timer);
      pendingReset.current = null;
    }
    return () => {
      if (ranKey.current !== targetKey) return;
      const key = targetKey;
      // Only the run current now: if anything claims the store before the timer fires (a reopened
      // sheet's own run for the same target, Curate), that newer run isn't ours to reset.
      const runId = useSeedDiscoveryStore.getState().runId;
      const timer = setTimeout(() => {
        if (pendingReset.current?.timer === timer) pendingReset.current = null;
        if (ranKey.current === key) ranKey.current = null;
        const s = useSeedDiscoveryStore.getState();
        if (s.runId === runId) s.resetIfOwned(key);
      }, 0);
      pendingReset.current = { key, timer };
    };
  }, [targetKey]);

  // Pre-check the top candidates once per target.
  useEffect(() => {
    if (!candidates || !targetKey || precheckedFor.current === targetKey) return;
    precheckedFor.current = targetKey;
    const top = [...candidates].sort((a, b) => b.overlap_score - a.overlap_score).slice(0, PRECHECK);
    setChecked((cur) => {
      const nextSet = new Set(cur);
      for (const c of top) nextSet.add(c.paper_id);
      return nextSet;
    });
  }, [candidates, targetKey]);

  const cards: SeedCandidate[] = useMemo(() => {
    const out: SeedCandidate[] = [];
    const seen = new Set<string>();
    for (const c of [...(anchor ? [anchor] : []), ...extra, ...(candidates ?? [])]) {
      if (!seen.has(c.paper_id)) { seen.add(c.paper_id); out.push(c); }
    }
    return out;
  }, [anchor, extra, candidates]);
  /** The checked seeds, in card order — what Start (Task 9) adds and trawls. */
  const checkedSeeds: SeedCandidate[] = useMemo(() => cards.filter((c) => checked.has(c.paper_id)), [cards, checked]);
  const previewSeeds: SeedRef[] = useMemo(
    () => checkedSeeds.map((c) => ({ key: c.paper_id, paper_id: c.paper_id, doi: c.doi, title: c.title })),
    [checkedSeeds],
  );
  const effScope = useMemo(() => effectiveScope({ scope }, concept.scope), [scope, concept.scope]);
  // Wait for the preview's final answer on every checked seed so Check & add's sample isn't missing any.
  const previewPending = previewSeeds.some((s) => !isSettled(setupStatus[netKey(s.key, effScope.recency)]));
  const seedsValid = checkedSeeds.length > 0 && !previewPending;
  // The seed preview (networks fetched as on Start; nothing posted) — from Seeds on only, so an
  // anchor picked on Describe doesn't fetch before the user gets there (as TrawlPreview did).
  const shownSeeds = step >= 2 ? previewSeeds : NO_SEEDS;
  const preview = useSeedPreview(shownSeeds, effScope);
  const paperKeys = usePaperKeys(concept.id);
  const k = shownSeeds.length;
  const addedLine: ReactNode = step === 1 ? "New papers: pick seeds next"
    : k === 0 ? "New papers: tick a seed to count them"
    : preview.settled < k || (!paperKeys.data && !paperKeys.isError) ? `Counting new papers… (${preview.settled} of ${k})`
    : paperKeys.data
      ? `~${newPaperCount(preview.merged, effScope, paperKeys.data)} new, from ${k} seed${k === 1 ? "" : "s"}`
      // The concept's ids didn't load: the preview's reach, without the "already in" check.
      : `~${preview.stats.inRange} papers from ${k} seed${k === 1 ? "" : "s"} (some may be in your list already)`;
  const showAdds = step === 1 ? !!draft && !seedsOnly : true;

  function toggle(key: string) {
    setChecked((cur) => {
      const nextSet = new Set(cur);
      if (nextSet.has(key)) nextSet.delete(key); else nextSet.add(key);
      return nextSet;
    });
  }

  function addByHand(p: PickedPaper) {
    const c = toAnchorCandidate(p);
    setExtra((xs) => [{ ...c, reason: "Added by you" }, ...xs]);
    setChecked((cur) => new Set(cur).add(c.paper_id));
  }

  function next() {
    if (step === 1 && draftValid && scopeValid) { setScope(overridesOnly(scope)); setStep(2); }
    else if (step === 2 && seedsValid) { setSampleReady(false); setStep(3); }
  }

  async function run(pq: ProjectQuestion | undefined) {
    if (runBusy.current) return; // a second click while the first Start runs: change nothing
    runBusy.current = true;
    try {
      lastPq.current = pq;
      const id = await addAngle.start({
        ...(draft && mode.kind === "new"
          ? { draft: { label: draft.label, question: draft.question, search_terms: draft.search_terms, auto_drafted: draft.auto_drafted, source: draft.source }, scope: overridesOnly(scope) }
          : {}),
        checked: checkedSeeds,
        projectQuestion: pq,
      });
      if (!mounted.current) return;
      setReviewing(false);
      if (!id) return; // Error: the sheet and every input stay; the footer offers Retry.
      clearTags(concept.id); // the angle exists now: its real scores replace the sample's tags
      if (ranKey.current) useSeedDiscoveryStore.getState().resetIfOwned(ranKey.current);
      onClose();
      onDone?.(id);
    } finally {
      runBusy.current = false;
    }
  }

  function leave() {
    if (!strandedId) return;
    if (ranKey.current) useSeedDiscoveryStore.getState().resetIfOwned(ranKey.current);
    onClose();
    onDone?.(strandedId);
  }

  function onStart() {
    if (running) return;
    // A retry resumes the same run (the angle may already exist): no second review.
    if (addAngle.state === "error" || mode.kind === "seeds") void run(lastPq.current);
    else setReviewing(true);
  }

  const nextDisabled = (step === 1 && !(draftValid && scopeValid)) || (step === 2 && !seedsValid);
  // What a disabled Next / Start / Back is waiting for (slice 4): a visible line, since disabled
  // buttons get no hover and keyboard users never see their tooltips.
  const seedsReason = checkedSeeds.length === 0 ? "Tick at least one seed."
    : previewPending ? "Checking what these seeds would add…" : null;
  const nextReason = step === 1 ? (!draft ? "Draft the angle, or use your words, to continue."
    : !draftValid ? "Give the angle a label and a question."
    : !scopeValid ? "Fix the years above to continue." : null)
    : step === 2 ? seedsReason : null;
  const startReason = step === 3 ? (sampleReady ? null : "Grading a sample… Start unlocks when it's done.") : seedsReason;
  const backLocked = !!createdId && step <= 2;
  const atStart = step === 3 || (seedsOnly && step === 2);
  const startReady = step === 3 ? sampleReady : seedsValid;
  const fitResult = fit?.status === "done" ? fit.result : undefined;
  const reestimate = fitEdited ? (
    <span className="ml-1">Edited · <Button variant="link" size="sm" className="h-auto p-0" disabled={!draftValid}
      onClick={() => draft && runEstimate(draft)}>Re-estimate</Button></span>
  ) : null;
  const dim = fitEdited ? "opacity-60" : undefined;
  const fitLine: ReactNode = seedsOnly || !fit ? null
    : fit.status === "running" ? <><span className={dim}>Checking your papers against this angle…</span>{reestimate}</>
    : fit.status === "error" ? (
      <>
        <span className={dim}>{fit.detail ?? "Couldn't estimate."}</span>
        {reestimate ?? (!fit.detail && (
          <Button variant="link" size="sm" className="ml-1 h-auto p-0" onClick={() => draft && runEstimate(draft)}>Try again</Button>
        ))}
      </>
    )
    : !fitResult || fitResult.eligible === 0 ? null
    : (
      <>
        <span className={dim}>
          {fitLineText(fitResult)}
        </span>
        {reestimate}
        {fitIds.length > 0 && <p className="mt-0.5 text-xs text-muted-foreground">Tagged in your list: the sampled papers that fit</p>}
      </>
    );
  const adds = showAdds ? <WhatItAdds fit={fitLine} added={addedLine} /> : null;

  return (
    <div
      data-testid="add-angle-sheet"
      data-source={draft?.source ?? baseSource}
      className="flex min-h-0 flex-1 flex-col"
    >
      {!seedsOnly && <StepRail step={step} />}
      <div className="flex-1 overflow-y-auto p-5">
        {step === 1 && (
          <div className="flex flex-col gap-5">
            <DraftStep
              text={text} setText={setText} draft={draft} setDraft={setDraft} anchor={anchor}
              drafting={drafting} gap={gap}
              onDraft={() => runDraft({ name: concept.name, text: text.trim() }, anchor ? "from_paper" : baseSource)}
              onUseMyWords={useMyWords} onPickPaper={pickPaper}
            />
            {adds}
            <div className="flex flex-col gap-4">
              <div data-testid="scope-line" className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
                <span>
                  {overridden ? `Scope: ${scopeText(effScope)} (this angle only)` : `Scope: same as the project — ${scopeText(concept.scope)}`}
                </span>
                {(!scopeShown || scopeValid) && (
                  <Button variant="link" size="sm" className="h-auto p-0" aria-expanded={scopeShown} aria-controls="add-angle-scope"
                    onClick={() => setScopeOpen(!scopeShown)}>
                    {scopeShown ? "Done" : "Change"}
                  </Button>
                )}
                {overridden && (
                  <Button variant="link" size="sm" className="h-auto p-0"
                    onClick={() => { setScope({}); setScopeOpen(false); }}>
                    Use the project's
                  </Button>
                )}
              </div>
              {scopeShown && (
                <div id="add-angle-scope" className="rounded-md border p-4">
                  <ScopeOverrides concept={concept} scope={scope} setScope={setScope} outOfBounds={outOfBounds} inverted={inverted} />
                </div>
              )}
            </div>
          </div>
        )}
        {step === 2 && (
          <div data-testid="seeds-step" data-scope={JSON.stringify(overridesOnly(scope))} className="flex min-w-0 flex-col gap-4">
            <p className="text-sm text-muted-foreground">
              Seeds for <span className="font-medium text-foreground">{draft?.label}</span>. We follow what they cite and what cites them.
            </p>
            {adds}
            {blocked && !own && (
              <div role="status" className="flex flex-col gap-2 rounded-md border p-3 text-sm">
                <p>Seed discovery for the whole project is still running — try again when it finishes</p>
                <div>
                  <Button size="sm" variant="outline" onClick={() => claimDiscovery(false)}>Retry</Button>
                </div>
              </div>
            )}
            {own && storeRunning && <p aria-live="polite" className="text-sm">Finding seed papers… ({storePhase})</p>}
            {own && storeError && <p role="alert" className="text-sm text-destructive">Discovery didn't work this time: {storeError}</p>}
            <ul className="flex flex-col gap-2">
              {cards.map((c) => (
                <li key={c.paper_id} className="flex gap-3 rounded-md border p-3 text-sm">
                  <Checkbox id={`angle-seed-${c.paper_id}`} checked={checked.has(c.paper_id)}
                    onCheckedChange={() => toggle(c.paper_id)} aria-label={c.title} />
                  <label htmlFor={`angle-seed-${c.paper_id}`} className="flex flex-col gap-1">
                    <span className="font-medium">{c.title}</span>
                    <span className="text-muted-foreground">
                      {[c.year, c.journal].filter(Boolean).join(" · ")}
                    </span>
                    {c.reason && <span>{c.reason}</span>}
                  </label>
                </li>
              ))}
            </ul>
            {own && (storeError || (!storeRunning && candidates?.length === 0)) && (
              <div className="flex flex-col gap-3 rounded-md border p-4">
                <p className="text-sm">No suggestions to show. Add a paper you know.</p>
                <PaperSearchBox label="Add a paper by title or DOI" onPick={addByHand} />
              </div>
            )}
            {own && !storeRunning && target && (
              <div>
                <Button size="sm" variant="outline" onClick={() => claimDiscovery(true)}>Rediscover</Button>
              </div>
            )}
            <SeedWarnings seeds={shownSeeds} preview={preview} remaining={me?.plan_limits.scoring_per_concept ?? null} />
          </div>
        )}
        {step === 3 && draft && (
          <div className="flex flex-col gap-4">
            {adds}
            <SampleGrades conceptId={concept.id} label={draft.label} question={draft.question}
              seeds={previewSeeds} scope={effScope} onSettled={() => setSampleReady(true)} />
          </div>
        )}
        {error && <p role="alert" className="mt-3 text-sm text-destructive">{error}</p>}
        {addAngle.state === "error" && (
          <p role="alert" className="mt-3 text-sm text-destructive">
            {addAngle.failure
              ? addAngle.failure.reason
              : "Starting didn't finish. Your choices are kept; Retry picks up where it stopped."}
          </p>
        )}
        {createdId && !addAngle.failure && (
          <p className="mt-3 text-sm text-muted-foreground">This angle is saved — Retry adds its seeds.</p>
        )}
        {strandedId && !running && (
          <div className="mt-3">
            <Button variant="outline" size="sm" onClick={leave}>Leave it — finish seeds later</Button>
          </div>
        )}
      </div>
      <div className="flex items-center justify-between gap-3 border-t p-4">
        {step > firstStep ? (
          <div className="flex items-center gap-3">
            <Button variant="ghost" disabled={running || backLocked} aria-describedby={backLocked && !running ? "add-angle-back-why" : undefined}
              onClick={() => { setError(null); setStep((s) => (s - 1) as Step); }}>Back</Button>
            {backLocked && !running && (
              <p id="add-angle-back-why" className="text-xs text-muted-foreground">This angle is saved, so earlier steps can't change.</p>
            )}
          </div>
        ) : <span />}
        {atStart ? (
          <div className="flex items-center gap-3">
            {!running && !startReady && startReason && (
              <p id="add-angle-start-why" className="text-xs text-muted-foreground">{startReason}</p>
            )}
            {running && (
              <p aria-live="polite" className="flex items-center gap-1.5 text-sm text-muted-foreground">
                <Loader2 className="size-3.5 animate-spin" />{RUNNING_COPY[addAngle.state]}
              </p>
            )}
            {!addAngle.failure?.upgrade && (
              <Button data-testid="start-angle" data-ready={String(startReady)} disabled={!startReady || running} onClick={onStart}
                aria-describedby={!running && !startReady && startReason ? "add-angle-start-why" : undefined}>
                {addAngle.state === "error" ? "Retry" : "Start"}
              </Button>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-3">
            {nextDisabled && nextReason ? <p id="add-angle-next-why" className="text-xs text-muted-foreground">{nextReason}</p>
              : step === 1 && <p className="text-xs text-muted-foreground">Your project question stays as it is unless you choose to redraft it</p>}
            <Button disabled={nextDisabled} onClick={next} className="shrink-0"
              aria-describedby={nextDisabled && nextReason ? "add-angle-next-why" : undefined}>{step === 1 ? "Pick seeds →" : "Next"}</Button>
          </div>
        )}
      </div>
      {reviewing && draft && (
        <ProjectQuestionReview
          open conceptId={concept.id}
          angles={[...(concept.facet_list ?? []), draft].map((f) => ({ label: f.label, question: f.question }))}
          currentAbstract={concept.abstract} currentKeywords={concept.keywords}
          title={`Add ${draft.label}?`} confirmLabel="Add" submitting={running}
          onSubmit={(pq) => void run(pq)} onCancel={() => { if (!running) setReviewing(false); }}
        />
      )}
    </div>
  );
}

/** Where you are in the three steps. Display only: Back is the way back, so no step's checks get skipped. */
function StepRail({ step }: { step: Step }) {
  return (
    <ol aria-label="Steps" className="flex gap-2 border-b px-5 py-3">
      {STEPS.map((n) => {
        const state = n < step ? "done" : n === step ? "current" : "todo";
        return (
          <li key={n} aria-current={state === "current" ? "step" : undefined}
            className={`flex flex-1 items-center gap-2 text-xs ${state === "current" ? "font-semibold text-foreground" : "text-muted-foreground"}`}>
            <span className={`inline-flex size-5 shrink-0 items-center justify-center rounded-full border text-[11px] ${
              state === "done" ? "border-[#a6e3a1] bg-[#a6e3a1] text-[#11111b]"
                : state === "current" ? "border-primary text-primary" : "border-muted-foreground/40"}`}>
              {state === "done" ? <Check className="size-3" aria-label="done" /> : n}
            </span>
            {STEP_NAMES[n]}
            {n < 3 && <span aria-hidden className="h-px flex-1 bg-border" />}
          </li>
        );
      })}
    </ol>
  );
}

const toSamplePaper = (x: PreviewPaper) => ({
  key: paperKey(x.paper), title: x.paper.title, authors: x.paper.authors ?? [], year: x.paper.year ?? null,
  abstract: x.paper.abstract ?? null, journal: x.paper.journal ?? "", citations: x.paper.citations ?? 0,
  doi: x.paper.doi ?? null,
});

/** ③ Check & add: grade a sample of the preview against [...active, this angle] — one call, saves nothing. */
function SampleGrades({ conceptId, label, question, seeds, scope, onSettled }: {
  conceptId: string;
  label: string;
  question: string;
  seeds: SeedRef[];
  scope: ConceptScope;
  onSettled: () => void;
}) {
  const networks = useSetupStore((s) => s.networks);
  const resolved = useSetupStore((s) => s.resolved);
  const sample = useMemo(
    () => pickSample(mergeNetworks(networksFor({ networks, resolved }, seeds, scope.recency)), scope, SAMPLE_SIZE),
    [networks, resolved, seeds, scope],
  );
  const grade = useSampleGradeDraft(conceptId);
  const [grades, setGrades] = useState<Map<string, DraftGrade> | null>(null);
  const [pending, setPending] = useState(sample.length > 0);
  const [failed, setFailed] = useState<string | null>(null);
  // The one grade request for this visit. Rendering from local state fed by its promise (not
  // grade.isPending/data) survives StrictMode's mount→cleanup→mount, where the mutation fired in
  // the first mount loses its observer; the ref keeps the second mount from re-firing the LLM call.
  const inflight = useRef<Promise<DraftGrade[]> | null>(null);

  useEffect(() => {
    if (!sample.length) { onSettled(); return; }
    inflight.current ??= grade.mutateAsync({ angle: { label, question }, papers: sample.map(toSamplePaper) });
    let live = true;
    inflight.current.then(
      (res) => {
        if (!live) return;
        setGrades(new Map(res.map((g) => [g.key, g])));
        setPending(false);
        onSettled();
      },
      async (err) => {
        const { status } = await httpError(err);
        if (!live) return;
        setFailed(status === 429 || status === 502
          ? "Sample grading isn't available right now — you can still start"
          : "Sample grading didn't work this time — you can still start");
        setPending(false);
        onSettled();
      },
    );
    return () => { live = false; };
    // Once per visit to Check & add: Next waits for the preview, so the sample is final here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div data-testid="sample-step" className="flex flex-col gap-4">
      <p className="text-sm text-muted-foreground">
        How a few of these papers would grade with this angle added. Nothing is saved yet.
      </p>
      {sample.length === 0 && <p className="text-sm">No preview papers to grade yet. You can still start.</p>}
      {pending && <p aria-live="polite" className="text-sm">Grading {sample.length} sample papers…</p>}
      <ul className="flex flex-col gap-2">
        {sample.map((x) => {
          const g = grades?.get(paperKey(x.paper));
          return (
            <li key={paperKey(x.paper)} className="flex flex-col gap-1 rounded-md border p-3 text-sm">
              <span className="font-medium">{x.paper.title}</span>
              <span className="text-muted-foreground">{x.paper.year ?? "n.d."}</span>
              {g && (
                <div className="flex flex-wrap gap-2">
                  <Badge variant="secondary">Overall {g.score}</Badge>
                  {g.angle_score != null && <Badge variant="outline">{label} {g.angle_score}</Badge>}
                </div>
              )}
              {g && <span>{g.justification}</span>}
            </li>
          );
        })}
      </ul>
      {failed && <p role="alert" className="text-sm">{failed}</p>}
    </div>
  );
}

function DraftStep({ text, setText, draft, setDraft, anchor, drafting, gap, onDraft, onUseMyWords, onPickPaper }: {
  text: string;
  setText: (t: string) => void;
  draft: DraftState | null;
  setDraft: (d: DraftState) => void;
  anchor: SeedCandidate | null;
  drafting: boolean;
  gap?: GapDraftInput;
  onDraft: () => void;
  onUseMyWords: () => void;
  onPickPaper: (p: PickedPaper) => void;
}) {
  return (
    <div className="flex flex-col gap-5">
    <div className="flex min-w-0 flex-col gap-5">
      {gap && (
        <p className="rounded-md bg-muted p-3 text-sm">
          From a coverage gap: <span className="font-medium">{gap.tag}</span>
          {drafting && !draft ? ". Drafting an angle from it…" : ""}
        </p>
      )}
      <div className="flex flex-col gap-2">
        <Label htmlFor="add-angle-text">Describe this angle</Label>
        <Textarea id="add-angle-text" rows={3} maxLength={QUESTION_MAX} value={text} onChange={(e) => setText(e.target.value)}
          placeholder="In a sentence or two, what should this angle find?" />
        <div className="flex flex-wrap gap-2">
          <Button size="sm" disabled={!text.trim() || drafting} onClick={onDraft}>{drafting ? "Drafting…" : "Draft it"}</Button>
          <Button size="sm" variant="outline" disabled={!text.trim()} onClick={onUseMyWords}>Use my words</Button>
        </div>
        {!text.trim() && <p className="text-xs text-muted-foreground">Write a sentence first, then draft it or use it as is.</p>}
      </div>

      <div className="flex flex-col gap-2">
        <PaperSearchBox onPick={onPickPaper} label="Or start from a paper" />
        {anchor && (
          <p className="text-sm">
            Starting from <span className="font-medium">{anchor.title}</span>{drafting ? ". Drafting an angle from it…" : ""}
          </p>
        )}
      </div>

    </div>
    <div className="min-w-0">
      {draft ? (
        <div className="flex flex-col gap-3 rounded-md border p-4">
          <p className="text-sm font-medium">The angle. Edit anything that's off.</p>
          <div className="space-y-1.5">
            <Label htmlFor="add-angle-label">Label</Label>
            <Input id="add-angle-label" maxLength={LABEL_MAX} value={draft.label}
              onChange={(e) => setDraft({ ...draft, label: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="add-angle-question">Question</Label>
            <Textarea id="add-angle-question" rows={4} maxLength={QUESTION_MAX} value={draft.question}
              onChange={(e) => setDraft({ ...draft, question: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="add-angle-terms">Search terms</Label>
            <Input id="add-angle-terms" maxLength={TERMS_MAX} value={draft.search_terms}
              onChange={(e) => setDraft({ ...draft, search_terms: e.target.value })} />
          </div>
        </div>
      ) : (
        <p className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
          Your angle appears here: label, question and search terms, ready to edit.
        </p>
      )}
    </div>
    </div>
  );
}

function ScopeOverrides({ concept, scope, setScope, outOfBounds, inverted }: {
  concept: Concept;
  scope: ConceptScope;
  setScope: (s: ConceptScope) => void;
  outOfBounds: boolean;
  inverted: boolean;
}) {
  const inherited = concept.scope;
  const [showYears, setShowYears] = useState(scope.year_min != null || scope.year_max != null);

  return (
    <div className="flex flex-col gap-6">

      <div className="flex flex-col gap-2">
        <h3 className="text-sm font-medium">Years</h3>
        <p className="text-xs text-muted-foreground">Papers outside the range are kept but hidden for this angle.</p>
        {!showYears ? (
          <div>
            <Button size="sm" variant="outline" onClick={() => setShowYears(true)}>Use different years</Button>
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-3">
              <Label htmlFor="add-angle-year-from">From</Label>
              <Input id="add-angle-year-from" type="number" min={YEAR_MIN} max={YEAR_MAX} className="w-28"
                placeholder={inherited?.year_min != null ? String(inherited.year_min) : "Any"}
                value={scope.year_min ?? ""} onChange={(e) => setScope({ ...scope, year_min: toYear(e.target.value) })} />
              <Label htmlFor="add-angle-year-to">To</Label>
              <Input id="add-angle-year-to" type="number" min={YEAR_MIN} max={YEAR_MAX} className="w-28"
                placeholder={inherited?.year_max != null ? String(inherited.year_max) : "Any"}
                value={scope.year_max ?? ""} onChange={(e) => setScope({ ...scope, year_max: toYear(e.target.value) })} />
            </div>
            <div>
              <Button size="sm" variant="ghost" onClick={() => {
                setScope({ ...scope, year_min: undefined, year_max: undefined });
                setShowYears(false);
              }}>
                Use the project's years
              </Button>
            </div>
            {outOfBounds && <p role="alert" className="text-sm text-destructive">Years must be between {YEAR_MIN} and {YEAR_MAX}.</p>}
            {!outOfBounds && inverted && <p role="alert" className="text-sm text-destructive">"From" must be on or before "To".</p>}
          </>
        )}
      </div>

      <fieldset className="flex flex-col gap-2">
        <legend className="text-sm font-medium">Classic ↔ Recent</legend>
        <p className="text-xs text-muted-foreground">
          Tilts this angle's search toward what its seeds cite (classic) or what cites them (recent).
        </p>
        <div className="flex flex-wrap gap-4">
          <label className="flex items-center gap-1.5 text-sm">
            <input type="radio" name="add-angle-recency" checked={scope.recency === undefined}
              onChange={() => setScope({ ...scope, recency: undefined })} />
            Inherit
          </label>
          {RECENCY.map((r) => (
            <label key={r} className="flex items-center gap-1.5 text-sm">
              <input type="radio" name="add-angle-recency" checked={scope.recency === r}
                onChange={() => setScope({ ...scope, recency: r })} />
              {RECENCY_LABEL[r]}
            </label>
          ))}
        </div>
      </fieldset>

      <div className="flex flex-col gap-2">
        <Label htmlFor="add-angle-field">Field</Label>
        <p className="text-xs text-muted-foreground">Steers which papers search turns up. It never hides anything.</p>
        <select id="add-angle-field" className="h-9 rounded-md border bg-background px-2 text-sm"
          value={scope.field ?? ""}
          onChange={(e) => setScope({ ...scope, field: e.target.value ? (e.target.value as FieldKey) : undefined })}>
          <option value="">Inherit ({fieldText(inherited)})</option>
          {FIELD_KEYS.map((k) => <option key={k} value={k}>{FIELD_GROUPS[k].label}</option>)}
        </select>
      </div>
    </div>
  );
}

type SeedPreview = ReturnType<typeof useSeedPreview>;

/** ② seed-status warnings (worded as TrawlPreview had them): still looking, S2 retrying,
 *  seeds with no citations, seeds S2 never answered for, and the plan's grading limit. */
function SeedWarnings({ seeds, preview, remaining }: { seeds: SeedRef[]; preview: SeedPreview; remaining: number | null }) {
  const { settled, retrying, empty, failed, stats } = preview;
  const note = quotaNote(stats.inRange, remaining);
  if (!seeds.length) return null;
  if (settled === seeds.length && !retrying && !empty.length && !failed.length && !note) return null;
  return (
    <div className="flex flex-col gap-1 rounded-md bg-muted p-4 text-sm" aria-live="polite">
      {settled < seeds.length && <p>Looking at what your seeds connect to… ({settled} of {seeds.length})</p>}
      {retrying > 0 && <p>Semantic Scholar is busy. Retrying {retrying === 1 ? "1 seed" : `${retrying} seeds`}…</p>}
      {empty.length > 0 && (
        <div>
          <p>We couldn't find citations for these. Each stays a seed but won't be trawled:</p>
          <ul className="list-disc pl-5">{empty.map((s) => <li key={s.key}>{s.title}</li>)}</ul>
        </div>
      )}
      {failed.length > 0 && (
        <div>
          <p>Semantic Scholar didn't answer for these, even after retrying. Each stays a seed but won't be trawled (change your selection to try again):</p>
          <ul className="list-disc pl-5">{failed.map((s) => <li key={s.key}>{s.title}</li>)}</ul>
        </div>
      )}
      {note && <p>{note}</p>}
    </div>
  );
}
